/**
 * Capacitor native bridge — detects native platform and provides
 * native push notifications, status bar, splash screen, haptics.
 * Falls back gracefully in the browser (PWA mode).
 */
import { Capacitor } from '@capacitor/core';

export const isNativePlatform = Capacitor.isNativePlatform();
export const platform = Capacitor.getPlatform(); // 'ios' | 'android' | 'web'

/** Hide the native splash screen after the app is ready */
export async function hideSplash() {
  if (!isNativePlatform) return;
  try {
    const { SplashScreen } = await import('@capacitor/splash-screen');
    await SplashScreen.hide();
  } catch (e) {
    console.warn('SplashScreen hide failed:', e);
  }
}

/** Set status bar style (light text on dark bg) */
export async function configureStatusBar() {
  if (!isNativePlatform) return;
  try {
    const { StatusBar, Style } = await import('@capacitor/status-bar');
    await StatusBar.setStyle({ style: Style.Light });
    if (platform === 'android') {
      await StatusBar.setBackgroundColor({ color: '#0A0A0A' });
    }
  } catch (e) {
    console.warn('StatusBar config failed:', e);
  }
}

/**
 * Register for native push notifications (iOS / Android).
 * Returns the device token or null if unavailable.
 */
export async function registerNativePush() {
  if (!isNativePlatform) return null;
  try {
    const { PushNotifications } = await import('@capacitor/push-notifications');

    const permResult = await PushNotifications.requestPermissions();
    if (permResult.receive !== 'granted') {
      console.log('Native push permission denied');
      return null;
    }

    await PushNotifications.register();

    return new Promise((resolve) => {
      PushNotifications.addListener('registration', (token) => {
        console.log('Native push token:', token.value);
        resolve(token.value);
      });

      PushNotifications.addListener('registrationError', (err) => {
        console.error('Native push registration error:', err);
        resolve(null);
      });

      // Handle received notifications while app is in foreground
      PushNotifications.addListener('pushNotificationReceived', (notification) => {
        console.log('Push received in foreground:', notification);
      });

      // Handle notification tap (app was in background)
      PushNotifications.addListener('pushNotificationActionPerformed', (notification) => {
        console.log('Push notification tapped:', notification);
        const url = notification.notification?.data?.url;
        if (url) {
          window.location.href = url;
        }
      });
    });
  } catch (e) {
    console.warn('Native push registration failed:', e);
    return null;
  }
}

/** Trigger a light haptic feedback on native */
export async function hapticTap() {
  if (!isNativePlatform) return;
  try {
    const { Haptics, ImpactStyle } = await import('@capacitor/haptics');
    await Haptics.impact({ style: ImpactStyle.Light });
  } catch (e) {
    // silent
  }
}
